import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Grid,
  FormHelperText,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { Send as SendIcon } from "@mui/icons-material";
import { toast } from "react-toastify";

const AddForm = ({
  value = {
    type: "chatform",
    header: "",
    body: "",
    footer: "",
    formId: "",
    buttonText: "",
    isEndpointPowered: false,
  },
  onUpdate,
}) => {
  const [formData, setFormData] = useState({
    header: value?.header ?? "",
    body: value?.body ?? "",
    footer: value?.footer ?? "",
    formId: value?.formId ?? "",
    buttonText: value?.buttonText ?? "",
    isEndpointPowered: value?.isEndpointPowered ?? false,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Validate form fields
  const validateForm = () => {
    const newErrors = {};
    if (!formData.header.trim()) newErrors.header = "Header text is required";
    if (!formData.body.trim()) newErrors.body = "Body text is required";
    if (!formData.footer.trim()) newErrors.footer = "Footer text is required";
    if (!formData.formId.trim()) newErrors.formId = "Form ID is required";
    if (!formData.buttonText.trim())
      newErrors.buttonText = "Button text is required";
    return newErrors;
  };

  // Update errors when form data changes
  useEffect(() => {
    const validationErrors = validateForm();
    setErrors(validationErrors);
  }, [formData]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSwitchChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      isEndpointPowered: e.target.checked,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fill in all required fields");
      setIsSubmitting(false);
      return;
    }

    try {
      await onUpdate({ ...formData, type: "chatform" });
      toast.success("Form saved successfully");
    } catch (error) {
      toast.error("Failed to save form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Form is valid when there are no errors
  const isFormValid = Object.keys(errors).length === 0;

  return (
    <Container maxWidth="md" sx={{ my: 2 }}>
      <Typography
        variant="body2"
        color="textSecondary"
        sx={{ mb: 4 }}
        align="center"
      >
        Fill in all required fields to create a form for user interaction
      </Typography>

      <Box component="form" onSubmit={handleSubmit} noValidate>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              size="small"
              fullWidth
              label="Header Text"
              name="header"
              value={formData.header}
              onChange={handleInputChange}
              required
              variant="outlined"
              placeholder="Enter header text"
              error={!!errors.header}
              helperText={errors.header}
              InputLabelProps={{ shrink: true }}
              aria-required="true"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Body Text"
              name="body"
              value={formData.body}
              onChange={handleInputChange}
              required
              variant="outlined"
              multiline
              rows={4}
              placeholder="Enter body content"
              error={!!errors.body}
              helperText={errors.body}
              InputLabelProps={{ shrink: true }}
              aria-required="true"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Footer Text"
              name="footer"
              value={formData.footer}
              onChange={handleInputChange}
              required
              variant="outlined"
              placeholder="Enter footer text"
              error={!!errors.footer}
              helperText={errors.footer}
              InputLabelProps={{ shrink: true }}
              aria-required="true"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Form ID"
              name="formId"
              value={formData.formId}
              onChange={handleInputChange}
              required
              variant="outlined"
              placeholder="Enter form ID"
              error={!!errors.formId}
              helperText={
                errors.formId || (
                  <Typography component="span">
                    To obtain form ID, click{" "}
                    <a
                      href="/home/flows"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: "inherit",
                        textDecoration: "underline",
                      }}
                    >
                      here
                    </a>
                  </Typography>
                )
              }
              InputLabelProps={{ shrink: true }}
              aria-required="true"
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              size="small"
              label="Button Text"
              name="buttonText"
              value={formData.buttonText}
              onChange={handleInputChange}
              required
              variant="outlined"
              placeholder="Enter button text"
              error={!!errors.buttonText}
              helperText={
                errors.buttonText || "Text for the form submission button"
              }
              InputLabelProps={{ shrink: true }}
              aria-required="true"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.isEndpointPowered}
                  onChange={handleSwitchChange}
                  color="primary"
                />
              }
              label="Endpoint Powered"
              sx={{ justifyContent: "space-between", ml: 0, mr: 0 }}
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={!isFormValid || isSubmitting}
              startIcon={
                isSubmitting ? <CircularProgress size={20} /> : <SendIcon />
              }
              sx={{
                py: 1.5,
                backgroundColor: "#1976d2",
                "&:hover": { backgroundColor: "#115293" },
              }}
              aria-label="Save form"
            >
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Typography
        variant="caption"
        color="textSecondary"
        sx={{ mt: 3, display: "block" }}
        align="center"
      >
        *All fields are required. Ensure the correct form ID is selected for
        your needs.
      </Typography>
    </Container>
  );
};

export default AddForm;
